import { Controller } from "stimulus"
import pluralize from "pluralize"

export default class extends Controller {
  static targets = [
    "quantityInput", "minimumQuantityMessageContainer",
    "checkoutFlashContainer", "cartToast", "toastProductDetails", "toastCartCount", "toastCartTotal",
    "matchesCount", "matchesContainer", "showMoreMatchesLink",
    "productShowContainer", "productDescription", "modalDescription", "varietyHeading", "cultivarHeading", "addToCartButton", "readMoreLink", "minQuantityLink", "quantityCalculatedContainer", "quantityCalculatedText", "totalPrice", "productListingContainer", "productListingVarietyList", "productListingCultivarList",
    "addToCartForm", "cartQuantityInput"
  ]

  static values = {
    allMinimumQuantity: Number
  }

  connect() {
    this.enableScrollDetectionForCartButtons()
    this.removeDescriptionUlMargins()
    this.transformFloatingHeadings()
    this._adjustLinksIn(this.productDescriptionTarget)
    this._normalizeAnchorsIn(this.productDescriptionTarget)

    // Also adjust variety/cultivar description cards
    this.element.querySelectorAll('.variety-desc-card, .cultivar-desc-card').forEach((el) => {
      this._adjustLinksIn(el)
      this._normalizeAnchorsIn(el)
    })

    this.setupQuantityInputListener()
    this.dispatchQuantityInputEventIfPresent()

    // Enhance links and headings inside modal when opened
    this.element.addEventListener('modal:opened', () => {
      if (this.hasModalDescriptionTarget) {
        // Normalize headings within modal content and adjust links
        this._transformFloatingHeadingsIn(this.modalDescriptionTarget)
        this._adjustLinksIn(this.modalDescriptionTarget)
        this._normalizeAnchorsIn(this.modalDescriptionTarget)
      }
    })
  }

  // -----------------------------
  // Basic Setup / Utility
  // -----------------------------

  removeDescriptionUlMargins() {
    this.productDescriptionTarget.querySelectorAll("ul").forEach((ul) => {
      const prevP = ul.previousElementSibling
      if (prevP?.tagName === "P") {
        prevP.style.margin = "0"
      }
    })
  }

  // Convert plain-text heading lines into semantic headings
  transformFloatingHeadings() {
    if (!this.hasProductDescriptionTarget) return
    this._transformFloatingHeadingsIn(this.productDescriptionTarget)
  }

  _transformFloatingHeadingsIn(container) {
    if (!container) return
    const headingPhrases = [
      /benefits of .* as a cover crop/i,
      /planting and management advice/i,
      /extra management tips/i
    ]

    const promoteToHeading = (text) => {
      if (!text) return null
      const trimmed = text.trim()
      if (!trimmed) return null
      const isKnown = headingPhrases.some((re) => re.test(trimmed))
      const looksLikeTitle = /^[A-Z][A-Za-z0-9 \-():]+$/.test(trimmed) && trimmed.length <= 120
      return (isKnown || looksLikeTitle) ? trimmed : null
    }

    // Handle both element paragraphs and orphan text nodes
    const nodes = Array.from(container.childNodes)
    nodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const headingText = promoteToHeading(node.textContent)
        if (headingText) {
          const h3 = document.createElement('h3')
          h3.textContent = headingText
          node.replaceWith(h3)
        }
      } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'P') {
        const headingText = promoteToHeading(node.textContent)
        if (headingText) {
          const h3 = document.createElement('h3')
          h3.textContent = headingText
          node.replaceWith(h3)
        }
      }
    })
  }

  _isExternalHref(href) {
    if (!href) return false
    if (href.startsWith('#')) return false
    if (href.startsWith('/')) return false
    try {
      const url = new URL(href, window.location.origin)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch (_) {
      return false
    }
  }

  _adjustLinksIn(el) {
    if (!el) return
    el.querySelectorAll('a[href]')?.forEach((a) => {
      const href = a.getAttribute('href') || ''
      if (!this._isExternalHref(href)) return
      // Skip our UI links
      if (a.classList.contains('blue-link')) return
      a.setAttribute('target', '_blank')
      a.setAttribute('rel', 'noopener')
    })
  }

  _normalizeAnchorsIn(el) {
    if (!el) return
    el.querySelectorAll('a[href]')?.forEach((a) => {
      const href = a.getAttribute('href') || ''
      if (!this._isExternalHref(href)) return
      // Skip our UI links
      if (a.classList.contains('blue-link')) return
      try {
        const url = new URL(href, window.location.origin)
        const host = url.hostname.replace(/^www\./, '')
        // Only normalize if the anchor doesn't contain nested elements and isn't already domain-labeled
        const hasElementChild = Array.from(a.childNodes).some((n) => n.nodeType === Node.ELEMENT_NODE)
        const alreadyDomain = a.textContent?.trim() === host
        if (!hasElementChild && !alreadyDomain) {
          a.textContent = host
          // Append a tiny external indicator
          const icon = document.createElement('span')
          icon.className = 'external-link-icon'
          icon.setAttribute('aria-hidden', 'true')
          icon.textContent = '↗'
          a.appendChild(icon)
        }
      } catch (_) {
        // ignore invalid URLs
      }
    })
  }

  expandHtmlSnippet(e) {
    e.preventDefault()
    const el = e.target
    const parent = el.parentElement
    if (!parent) return
    parent.innerHTML = el.dataset.fullContent
    // After expansion, adjust and normalize anchors
    this._adjustLinksIn(parent)
    this._normalizeAnchorsIn(parent)
  }

  dispatchQuantityInputEventIfPresent() {
    if (this.hasQuantityInputTarget) {
      this.quantityInputTarget.dispatchEvent(new Event("input"))
    }
  }

  enableScrollDetectionForCartButtons() {
    // If there's a .product-show-container, disable cart buttons while scrolling
    if (this.hasProductShowContainerTarget) {
      let isScrolling
      const handleScroll = () => {
        // Disable all cart buttons that aren't already disabled by other logic
        this.addToCartButtonTargets.forEach((button) => {
          // Store the previous disabled state if we haven't already
          if (!button.hasAttribute('data-prev-disabled')) {
            // Check both the attribute and the property since dynamic changes might only affect the property
            const wasDisabled = button.hasAttribute('disabled') || button.disabled
            button.setAttribute('data-prev-disabled', wasDisabled)
          }
          button.setAttribute('disabled', '')
          button.disabled = true
        })

        // Clear the existing timeout
        window.clearTimeout(isScrolling)

        // Set a timeout to run after scrolling ends (40ms)
        isScrolling = setTimeout(() => {
          // Re-enable only the buttons that weren't disabled before scrolling
          this.addToCartButtonTargets.forEach((button) => {
            const wasDisabled = button.getAttribute('data-prev-disabled') === 'true'
            if (!wasDisabled) {
              button.removeAttribute('disabled')
              button.disabled = false
            }
            button.removeAttribute('data-prev-disabled')
          })
        }, 40)
      }

      // Add scroll event listener
      window.addEventListener('scroll', handleScroll, false)
    }
  }

  onQuantityInputFocus() {
    if (this.hasQuantityInputTarget) {
      this.quantityInputTarget.value = ""
    }
  }

  onQuantityInputBlur(e) {
    if (this.hasQuantityInputTarget) {
      const allMinQuantity = Number(this.allMinimumQuantityValue)
      const userInputValue = e.target.value
      if (!userInputValue || userInputValue === "0") {
        this.quantityInputTarget.value = allMinQuantity
        this.quantityInputTarget.dispatchEvent(new Event("input"))
      }
    }
  }

  // No auto-expansion; keep condensed by default

  onReadMoreLinkClick(e) {
    e.preventDefault()
    const content = e.target.dataset.fullContent
    // Render expanded content and append an "Open full guide" affordance
    const container = e.target.parentElement
    container.innerHTML = content
    // Re-apply heading normalization
    // Re-run heading normalization on expanded content
    this.transformFloatingHeadings()

    // Make all links in the description open in a new tab for better UX
    this._adjustLinksIn(this.productDescriptionTarget)
    this._normalizeAnchorsIn(this.productDescriptionTarget)

    // Add a subtle inline "Open full guide" link after expansion
    const openFull = document.querySelector('#open-full-description')
    if (openFull) {
      const more = document.createElement('a')
      more.href = ''
      more.className = 'blue-link ml-2'
      more.textContent = 'Open full guide'
      more.addEventListener('click', (evt) => {
        evt.preventDefault()
        openFull.click()
      })
      container.appendChild(document.createTextNode(' '))
      container.appendChild(more)
    }
  }

  // -----------------------------
  // Quantity Input & Matches
  // -----------------------------

  setupQuantityInputListener() {
    if (!this.hasQuantityInputTarget) return

    // Handle "X lbs min" link
    this.minQuantityLinkTargets.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault()
        const linkQuantity = e.target.dataset.quantity
        if (this.hasQuantityInputTarget) {
          this.quantityInputTarget.value = linkQuantity
          this.quantityInputTarget.dispatchEvent(new Event("input"))
        }
      })
    })

    // Main input event
    this.quantityInputTarget.addEventListener("input", () => {
      this.onQuantityChange()
    })
  }

  onQuantityChange() {
    const newInputValue = Number(this.quantityInputTarget.value)
    const allOfferingsMinimumQuantity = Number(this.allMinimumQuantityValue)

    if (!newInputValue && this.hasMinimumQuantityMessageContainerTarget) {
      this.minimumQuantityMessageContainerTarget?.classList.remove("hidden")
      return
    }

    // Hide all variety and cultivar headings by default
    this.varietyHeadingTargets.forEach((heading) => {
      heading.classList.add('hidden')
    })
    this.cultivarHeadingTargets.forEach((heading) => {
      heading.classList.add('hidden')
    })

    // Check for minimum quantity
    if (newInputValue < allOfferingsMinimumQuantity) {
      // Show "no results" message
      if (this.hasMinimumQuantityMessageContainerTarget) {
        this.minimumQuantityMessageContainerTarget.classList.remove("hidden")
      }
      this.productListingContainerTargets.forEach((container) => {
        container.classList.add("hidden")
      })
      this._setMatchesCountTo(0)
      this._setShowMoreMatchesCountTo(
        this.productListingContainerTargets.length
      )
      this._showMoreMatchesLink()
      this._handleOnlyChildrenBorder()
    } else {
      // Hide "no results" message
      if (this.hasMinimumQuantityMessageContainerTarget) {
        this.minimumQuantityMessageContainerTarget.classList.add("hidden")
      }

      let matchingListingsCount = 0
      const allCultivarLists = this.productListingCultivarListTargets

      allCultivarLists.forEach((cultivarList) => {
        cultivarList.querySelectorAll("[data-product-show-target='productListingContainer']").forEach((container) => {
          const minimumQuantity = Number(container.dataset.minimumQuantity)
          if (newInputValue >= minimumQuantity) {
            this._makeListingVisible(container, newInputValue)

            // Show cultivar heading for this listing (only if cultivar data exists)
            if (cultivarList.dataset.cultivar) {
              const cultivarHeading = document.querySelector(
                `[data-product-show-target="cultivarHeading"][data-variety="${cultivarList.dataset.variety}"][data-cultivar="${cultivarList.dataset.cultivar}"]`
              )
              if (cultivarHeading) cultivarHeading.classList.remove("hidden")
            }

            // Show variety heading for this listing
            const varietyHeading = document.querySelector(
              `[data-product-show-target="varietyHeading"][data-variety="${cultivarList.dataset.variety}"]`
            )
            if (varietyHeading) varietyHeading.classList.remove("hidden")

            matchingListingsCount++
          } else {
            container.classList.add("hidden")
          }
        })
      })

      this._setMatchesCountTo(matchingListingsCount)
      const allListingsCount = this.productListingContainerTargets.length
      this._setShowMoreMatchesCountTo(allListingsCount - matchingListingsCount)
      if (allListingsCount - matchingListingsCount === 0) {
        this._hideMoreMatchesLink()
      } else {
        this._showMoreMatchesLink()
      }
      this._handleOnlyChildrenBorder()
    }
  }

  _makeListingVisible(elem, newInputValue) {
    // Reset states
    const quantityCalculatedText = elem.querySelector("[data-product-show-target='quantityCalculatedText']")
    const totalPriceElem = elem.querySelector("[data-product-show-target='totalPrice']")
    const cartButton = elem.querySelector("[data-product-show-target='addToCartButton']")
    const cartQuantityInput = elem.querySelector("[data-product-show-target='cartQuantityInput']")

    quantityCalculatedText.classList.remove("hidden")
    totalPriceElem.classList.remove("hidden")
    cartButton.classList.remove("hidden")
    elem.classList.remove("-less-than-min")
    elem.querySelector("[data-product-show-target='minQuantityLink']").classList.add("hidden")
    elem.classList.remove("hidden")

    /**
     * Calculate how many units.
     */
    const quantityCalculatedElem = elem.querySelector("[data-product-show-target='quantityCalculatedContainer']")
    const unit = quantityCalculatedElem.dataset.availableAs
    const maxQuantity = Number(cartButton.dataset.maxQuantity)
    const unitSize = Number(elem.dataset.unitSize)
    const maxQuantityAllowed = Math.min(newInputValue, maxQuantity)
    const remainder = maxQuantityAllowed % unitSize
    const toteOrBag = elem.dataset.toteOrBag === "true"
    const toteSize = Number(elem.dataset.toteSize)
    const bagSize = Number(elem.dataset.bagSize)

    let howMany = 0
    let actualQuantityToOrder = 0

    if (unit === "lb") {
      howMany = maxQuantityAllowed
      actualQuantityToOrder = maxQuantityAllowed
    } else {
      howMany = Math.floor(maxQuantityAllowed / unitSize)
      actualQuantityToOrder = maxQuantityAllowed - remainder
    }

    if (howMany === 0 && !toteOrBag) {
      howMany = Number((maxQuantityAllowed / unitSize).toFixed(2))
    }

    let howManyContent = ""
    if (toteOrBag) {
      const toteCount = Math.floor(maxQuantityAllowed / toteSize)
      const amountAfterTotes = Math.max(0, maxQuantityAllowed - toteCount * toteSize)
      const bagCount = Number((amountAfterTotes / bagSize).toFixed(2))

      // Check if unit is "lb" which means bulk purchasing is available
      const isBulkAvailable = unit === "lb"

      // For tote/bag products, only use bulk for large quantities (3+ totes worth)
      const shouldUseBulk = isBulkAvailable && maxQuantityAllowed >= (toteSize * 3)

      // For tote_or_bag products, calculate actual quantity differently
      if (shouldUseBulk) {
        // Use exact quantity for bulk orders (large quantities)
        actualQuantityToOrder = maxQuantityAllowed
      } else if (bagCount % 1 === 0) {
        // Whole number of bags
        actualQuantityToOrder = (toteCount * toteSize) + (bagCount * bagSize)
      } else {
        // For partial bags, round up to whole bags
        const wholeBagCount = Math.ceil(bagCount)
        actualQuantityToOrder = (toteCount * toteSize) + (wholeBagCount * bagSize)
      }

      // Display text calculations - show rounded up values when not using bulk
      if (toteCount > 0 && bagCount > 0) {
        if (!shouldUseBulk && bagCount % 1 !== 0) {
          // Show rounded up bag count for display
          const roundedBagCount = Math.ceil(bagCount)
          howManyContent = `${toteCount} ${pluralize("tote", toteCount)} & ${roundedBagCount} ${pluralize("bag", roundedBagCount)}`
        } else {
          howManyContent = `${toteCount} ${pluralize("tote", toteCount)} & ${bagCount % 1 === 0 ? Math.floor(bagCount) : bagCount} ${pluralize("bag", bagCount)}`
        }
      } else if (toteCount > 0) {
        howManyContent = `${toteCount} ${pluralize("tote", toteCount)}`
      } else if (bagCount > 0) {
        if (!shouldUseBulk && bagCount % 1 !== 0) {
          // Show rounded up bag count for display when not using bulk
          const roundedBagCount = Math.ceil(bagCount)
          howManyContent = `${roundedBagCount} ${pluralize("bag", roundedBagCount)}`
        } else {
          howManyContent = `${bagCount % 1 === 0 ? Math.floor(bagCount) : bagCount} ${pluralize("bag", bagCount)}`
        }
      }
    } else if (newInputValue !== maxQuantityAllowed) {
      howManyContent = `<span class="text-red-500 pr-1">${howMany % 1 === 0 ? Math.floor(howMany) : howMany.toFixed(2)} ${pluralize(unit, howMany)}</span>(max available)`
    } else if (unit.includes("lb") || remainder === 0) {
      howManyContent = `${howMany % 1 === 0 ? Math.floor(howMany) : howMany.toFixed(2)} ${pluralize(unit, howMany)}`
    } else {
      howManyContent = `<span class="text-red-500">${howMany % 1 === 0 ? Math.floor(howMany) : howMany.toFixed(2)} ${pluralize(
        unit,
        howMany
      )}</span>&nbsp;·&nbsp;${maxQuantityAllowed - remainder} ${pluralize(
        "lb",
        maxQuantityAllowed - remainder
      )}`
    }

    quantityCalculatedText.innerHTML = howManyContent

    // Use actualQuantityToOrder for cart quantity
    cartButton.dataset.quantity = actualQuantityToOrder.toString()
    if (cartQuantityInput) {
      cartQuantityInput.value = actualQuantityToOrder.toString()
    }

    // Update total price
    const pricePerUnit = Number(totalPriceElem.dataset.pricePerUnit)

    // For tote_or_bag, use the actualQuantityToOrder we calculated above
    const calculatedTotal = actualQuantityToOrder * pricePerUnit

    totalPriceElem.innerHTML = `$${calculatedTotal.toFixed(2).toLocaleString()}`
    if (calculatedTotal.toFixed(2).toLocaleString() === "0.00") {
      cartButton.setAttribute("disabled", "")
    } else {
      cartButton.removeAttribute("disabled")
    }
  }

  _handleOnlyChildrenBorder() {
    this.productListingContainerTargets.forEach((container) => {
      container.classList.remove("-only-child")
    })

    this.productListingCultivarListTargets.forEach((list) => {
      const visibleChildren = Array.from(list.children).filter(
        (child) => !child.classList.contains("hidden")
      )
      if (visibleChildren.length === 1) {
        visibleChildren[0].classList.add("-only-child")
      } else {
        visibleChildren.forEach((child) => child.classList.remove("-only-child"))
      }
    })
  }

  _setMatchesCountTo(count) {
    if (this.hasMatchesCountTarget) {
      this.matchesCountTarget.innerHTML = count
    }
  }

  _setShowMoreMatchesCountTo(count) {
    if (this.hasShowMoreMatchesLinkTarget) {
      this.showMoreMatchesLinkTarget.innerHTML = `Show ${count} more with higher minimum quantity`
    }
  }

  _showMoreMatchesLink() {
    if (this.hasShowMoreMatchesLinkTarget) {
      this.showMoreMatchesLinkTarget.classList.remove('hidden')
      if (this.hasMatchesContainerTarget) {
        this.matchesContainerTarget.classList.add('sticky', 'top-[85px]')
      }
    }
  }

  _hideMoreMatchesLink() {
    if (this.hasShowMoreMatchesLinkTarget) {
      this.showMoreMatchesLinkTarget.classList.add('hidden')
      if (this.hasMatchesContainerTarget) {
        this.matchesContainerTarget.classList.remove('sticky', 'top-[85px]')
      }
    }
  }

  onShowMoreMatchesClick(e) {
    e.preventDefault()
    this._hideMoreMatchesLink()

    // Update matches count
    const allListings = this.productListingContainerTargets
    this._setMatchesCountTo(allListings.length)

    // Show hidden matches + their state
    this.productListingContainerTargets.forEach((elem) => {
      if (!elem.classList.contains("hidden")) return

      // Hide quantity, total, and button (re-shown in partials)
      elem.querySelector("[data-product-show-target='quantityCalculatedText']").classList.add("hidden")
      elem.querySelector("[data-product-show-target='totalPrice']").classList.add("hidden")
      elem.querySelector("[data-product-show-target='addToCartButton']").classList.add("hidden")

      // Set bg color & show link for minimum quantity
      elem.classList.add("-less-than-min")
      elem.querySelector("[data-product-show-target='minQuantityLink']").classList.remove("hidden")

      // Finally unhide
      elem.classList.remove("hidden")
    })

    // Show all variety and cultivar headings
    this.varietyHeadingTargets.forEach((heading) => {
      heading.classList.remove("hidden")
    })
    this.cultivarHeadingTargets.forEach((heading) => {
      heading.classList.remove("hidden")
    })
  }

  // -----------------------------
  // Checkout Flash / Form Submission
  // -----------------------------

  showCheckoutFlash(productDetails = {}) {
    if (!this.hasCartToastTarget) return
    
    // Set product details in the toast
    if (this.hasToastProductDetailsTarget && productDetails.name) {
      const quantity = productDetails.quantity || '';
      const price = productDetails.price || '';
      const variety = productDetails.variety || '';
      const cultivar = productDetails.cultivar || '';
      
      let detailsText = `${productDetails.name}`;
      if (variety && variety !== 'Unspecified Variety (VNS)') {
        detailsText += ` - ${variety}`;
        if (cultivar && cultivar !== 'Common (Unbranded)') {
          detailsText += `, ${cultivar}`;
        }
      }
      if (quantity) {
        detailsText += ` (${quantity})`;
      }
      
      this.toastProductDetailsTarget.textContent = detailsText;
    }
    
    // Update cart count and total if provided
    if (this.hasToastCartCountTarget && productDetails.cartCount) {
      this.toastCartCountTarget.textContent = productDetails.cartCount;
    }
    if (this.hasToastCartTotalTarget && productDetails.cartTotal) {
      this.toastCartTotalTarget.textContent = productDetails.cartTotal;
    }
    
    // Show the toast with slide-in animation
    this.cartToastTarget.classList.remove('translate-x-full');
    this.cartToastTarget.classList.add('translate-x-0');
    
    // Auto-dismiss after 5 seconds
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.toastTimeout = setTimeout(() => {
      this.dismissToast();
    }, 5000);
  }
  
  dismissToast() {
    if (!this.hasCartToastTarget) return;
    
    this.cartToastTarget.classList.remove('translate-x-0');
    this.cartToastTarget.classList.add('translate-x-full');
    
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
      this.toastTimeout = null;
    }
  }

  async onAddToCartButtonClick(e) {
    e.preventDefault()
    const button = e.currentTarget
    if (button.disabled) return
    button.setAttribute("disabled", "")
    const form = button.form
    const originalButtonText = button.innerHTML

    const formData = new FormData(form)
    const data = {
      api_models_order_item: {
        quantity: formData.get("api_models_order_item[quantity]"),
        listing_id: formData.get("api_models_order_item[listing_id]")
      }
    }
    
    // Get product details for the toast
    const listingContainer = button.closest('[data-product-show-target="productListingContainer"]');
    const varietyList = listingContainer?.closest('[data-product-show-target="productListingCultivarList"]');
    const variety = varietyList?.dataset.variety || '';
    const cultivar = varietyList?.dataset.cultivar || '';
    const quantityText = listingContainer?.querySelector('[data-product-show-target="quantityCalculatedText"]')?.textContent || '';
    const totalPrice = listingContainer?.querySelector('[data-product-show-target="totalPrice"]')?.textContent || '';
    const productTitle = document.querySelector('h1')?.textContent?.trim() || 'Product';
    
    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify(data)
      })

      const responseData = await response.json()

      if (response.ok) {
        form.reset()
        
        // Update button with checkmark
        button.innerHTML = '✓ Added to Cart'
        button.classList.add('bg-green-700')
        
        // Show toast with product details
        this.showCheckoutFlash({
          name: productTitle,
          variety: variety,
          cultivar: cultivar,
          quantity: quantityText,
          price: totalPrice,
          cartCount: responseData.itemCount,
          cartTotal: responseData.cartTotal ? `$${responseData.cartTotal.toFixed(2)}` : null
        })
        
        // Update cart count badges with pulse animation
        document.querySelectorAll(".cart-count").forEach((count) => {
          count.innerHTML = responseData.itemCount
          // Add pulse animation
          count.classList.add('animate-pulse')
          setTimeout(() => {
            count.classList.remove('animate-pulse')
          }, 1000)
        })
        
        // Reset button text after 3 seconds
        setTimeout(() => {
          button.innerHTML = originalButtonText
          button.classList.remove('bg-green-700')
        }, 3000)
      } else {
        throw new Error(
          responseData.errors?.full_messages?.join(", ") ||
          responseData ||
          "Unknown error"
        )
      }
    } catch (error) {
      alert("There was an error adding this item to the cart.")
      if (window.Sentry) {
        window.Sentry.captureException("Error adding item to cart: " + error.message, {
          contexts: { data, errors: error }
        })
      }
    } finally {
      button.removeAttribute("disabled")
    }
  }
}